import { PaginatedResponse } from '../../types/api';
import { InterventionEditJson, InterventionJson } from '../../types/models';
import { CamionNetBaseApiClient } from '../base-client';

/**
 * Interventions API Types
 */
export interface GetInterventionsParams {
  filter?: string; // Example: "client_id||eq||123"
  limit?: number;
  page?: number; // 1-indexed
}

export type GetInterventionsResponse = PaginatedResponse<InterventionJson>;
export type GetInterventionByIdResponse = InterventionJson;

export interface CreateInterventionRequest {
  interior: boolean;
  exterior: boolean;
  disinfection: boolean;
  decontamination: boolean;
  package: boolean;
  comment: string;
  driver: string;
  photo_plate?: string;
  immatriculation: string;
  parc_number: string;
  immatriculation_bis?: string;
  parc_number_bis?: string;
  timestamp: string;
  prestation_costs_id: number;
  client_id: number;
}

export type CreateInterventionResponse = InterventionJson;

export interface UpdateInterventionRequest {
  interior?: boolean;
  exterior?: boolean;
  disinfection?: boolean;
  decontamination?: boolean;
  package?: boolean;
  comment?: string;
  driver?: string;
  photo_plate?: string;
  immatriculation?: string;
  parc_number?: string;
  immatriculation_bis?: string;
  parc_number_bis?: string;
  timestamp?: string;
  prestation_costs_id?: number;
}

export type UpdateInterventionResponse = InterventionJson;

export interface ValidateInterventionRequest {
  immatriculation: string;
  parc_number: string;
  client_id: number;
}

export interface ValidateInterventionResponse {
  isAlreadyWashedToday: boolean;
  similarImmat: {
    cnt: number;
    immatriculation: string;
    proximity: number;
  }[];
}

/**
 * Interventions API Endpoints
 */
export class InterventionsApi extends CamionNetBaseApiClient {
  /**
   * Get all interventions with optional filtering
   */
  async getInterventions(params?: GetInterventionsParams): Promise<GetInterventionsResponse> {
    const search = new URLSearchParams();
    if (params?.filter) {
      search.append('filter', params.filter);
    }
    if (params?.limit) {
      search.append('limit', String(params.limit));
    }
    if (params?.page) {
      search.append('page', String(params.page));
    }
    const qs = search.toString();
    const endpoint = qs ? `/api2/interventions?${qs}` : '/api2/interventions';
    return this.request<GetInterventionsResponse>(endpoint);
  }

  /**
   * Pagine automatiquement jusqu a recuperer tous les interventions matchant le filter.
   * Utilise le champ `total` renvoye par l'API pour savoir quand s'arreter.
   * Plafonne tout de meme a maxPages pour eviter une boucle infinie en cas de bug.
   */
  /**
   * Pagine en PARALLELE avec dedup par ID. Beaucoup plus rapide que la
   * version sequentielle pour les heavy users (sogaris ~10s -> ~1-2s).
   *
   * Strategie :
   *   1. Fetch page 1 -> on a `total` annonce par l API
   *   2. Calcule combien de pages restantes necessaires (cap par maxPages)
   *   3. Fetch toutes les pages restantes en parallele via Promise.all
   *      (le navigateur limite a ~6 connexions simultanees, donc le
   *      vrai facteur d acceleration est ~6x)
   *   4. Dedup par id pour gerer les eventuels doublons
   *   5. Si dedup detecte que toutes les pages renvoient les memes items,
   *      flag paginationBroken pour informer l UI
   */
  async getAllInterventions(params: { filter?: string; pageSize?: number; maxPages?: number } = {}): Promise<{
    data: InterventionJson[];
    total: number;
    pagesFetched: number;
    truncated: boolean;
    paginationBroken: boolean;
    duplicatesIgnored: number;
  }> {
    const pageSize = params.pageSize || 200;
    const maxPages = params.maxPages || 50; // 10 000 items max - couvre 6-12 mois pour heavy users

    // 1. Page 1
    const firstResp = await this.getInterventions({ filter: params.filter, limit: pageSize, page: 1 });
    const total = firstResp.total || 0;
    const seen = new Map<number, InterventionJson>();
    (firstResp.data || []).forEach((iv) => {
      if (iv && typeof iv.id === 'number') {
        seen.set(iv.id, iv);
      }
    });

    // Si tout tient sur la page 1, on s arrete
    if (total <= pageSize || seen.size >= total) {
      return {
        data: Array.from(seen.values()),
        total,
        pagesFetched: 1,
        truncated: total > 0 ? seen.size < total : false,
        paginationBroken: false,
        duplicatesIgnored: 0,
      };
    }

    // 2. Pages restantes a fetcher en parallele
    const totalPages = Math.ceil(total / pageSize);
    const pagesToFetch = Math.min(totalPages, maxPages);
    const promises: Promise<GetInterventionsResponse>[] = [];
    for (let p = 2; p <= pagesToFetch; p++) {
      promises.push(this.getInterventions({ filter: params.filter, limit: pageSize, page: p }));
    }

    const results = await Promise.all(promises);

    // 3. Dedup et detection pagination cassee
    let duplicatesIgnored = 0;
    let pagesWithoutNewItems = 0;
    results.forEach((resp) => {
      const data = resp.data || [];
      let addedThisPage = 0;
      data.forEach((iv) => {
        if (iv && typeof iv.id === 'number' && !seen.has(iv.id)) {
          seen.set(iv.id, iv);
          addedThisPage++;
        } else {
          duplicatesIgnored++;
        }
      });
      if (addedThisPage === 0) {
        pagesWithoutNewItems++;
      }
    });

    // Si plus de la moitie des pages n'a rien apporte de neuf, l API ne pagine pas
    const paginationBroken = results.length > 0 && pagesWithoutNewItems > results.length / 2;

    return {
      data: Array.from(seen.values()),
      total,
      pagesFetched: pagesToFetch,
      truncated: total > 0 ? seen.size < total : false,
      paginationBroken,
      duplicatesIgnored,
    };
  }

  /**
   * Get intervention by ID
   */
  async getInterventionById(interventionId: number): Promise<GetInterventionByIdResponse> {
    return this.request<GetInterventionByIdResponse>(`/api2/interventions/${interventionId}`);
  }

  /**
   * Create a new intervention
   */
  async createIntervention(data: InterventionEditJson): Promise<CreateInterventionResponse> {
    return this.request<CreateInterventionResponse>('/api2/interventions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update an existing intervention
   */
  async updateIntervention(interventionId: number, data: InterventionEditJson): Promise<UpdateInterventionResponse> {
    return this.request<UpdateInterventionResponse>(`/api2/interventions/${interventionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete an intervention by ID
   */
  async deleteIntervention(interventionId: number): Promise<{ affected: number }> {
    return this.request<{ affected: number }>(`/api2/interventions/${interventionId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Validate an intervention
   */
  async validateIntervention(data: ValidateInterventionRequest): Promise<ValidateInterventionResponse> {
    return this.request<ValidateInterventionResponse>('/api2/interventions/validate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}
